import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/contexts/authStore';
import { useIsFreeUser } from '@/api/hooks/useSubscription';
import { useFunnelClaim } from '@/api/hooks/useFunnel';
import {
  getFunnelToken,
  clearFunnelToken,
  canRetryClaim,
  markClaimAttempt,
} from '@/lib/funnelToken';

/**
 * Componente invisível montado no DashboardLayout.
 *
 * Fluxo do funil (/transcrever-instagram):
 * 1. Visitante anônimo gera preview 50% → token salvo no sessionStorage
 * 2. Cadastro + assinatura Starter (checkout Stripe)
 * 3. Ao voltar ao app com assinatura ativa, este watcher resgata a
 *    transcrição completa automaticamente e avisa o usuário.
 */
export default function FunnelClaimWatcher() {
  const user = useAuthStore((s) => s.user);
  const { isFree, isLoading } = useIsFreeUser();
  const claimMutation = useFunnelClaim();
  const queryClient = useQueryClient();
  const attemptedRef = useRef<string | null>(null);

  // Ao voltar ao app (ex: retorno do checkout Stripe), atualiza a assinatura
  useEffect(() => {
    const refetchSubscription = () => {
      void queryClient.invalidateQueries({ queryKey: ['subscription'] });
    };
    window.addEventListener('focus', refetchSubscription);
    document.addEventListener('visibilitychange', refetchSubscription);
    return () => {
      window.removeEventListener('focus', refetchSubscription);
      document.removeEventListener('visibilitychange', refetchSubscription);
    };
  }, [queryClient]);

  useEffect(() => {
    if (isLoading || !user) return;

    const token = getFunnelToken();
    if (!token) return;
    // Não tenta enquanto não houver assinatura ativa (evita 402 em loop)
    if (isFree) return;
    // Evita re-tentar o mesmo token repetidamente
    if (attemptedRef.current === token || !canRetryClaim()) return;

    attemptedRef.current = token;
    markClaimAttempt();

    claimMutation.mutate(token, {
      onSuccess: () => {
        clearFunnelToken();
        attemptedRef.current = null;
        toast.success(
          'Transcrição completa liberada e salva na sua biblioteca de inspirações!',
          { duration: 6000 }
        );
        // Garante refresh das listas de inspirações
        void queryClient.invalidateQueries({ queryKey: ['inspirations'] });
        void queryClient.invalidateQueries({ queryKey: ['projects'] });
      },
      onError: (error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        attemptedRef.current = null;
        if (status === 404) {
          // Token expirado/inválido: limpa para não tentar de novo
          clearFunnelToken();
        }
        // 402 e outros erros: mantém o token para nova tentativa (com throttle)
      },
    });
  }, [user, isFree, isLoading, claimMutation, queryClient]);

  return null;
}
